import {
 Box,
 Card,
 CardContent,
 Checkbox,
 CircularProgress,
 FormControlLabel,
 Tooltip,
 Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FieldSearch from '@/components/common/FieldSearch';
import { RolesDTO } from '@redux/identity/identityTypes';

interface RolesListProps {
 searchValue: string;
 setSearchValue: (value: string) => void;
 selectedGroupId: string | null;
 localRoles: RolesDTO[];
 localRoleIdSet: Set<string>;
 filteredGroupedRoles: [string, RolesDTO[]][];
 isLoadingRoles: boolean;
 handleGroupToggle: (roles: RolesDTO[]) => void;
 handleRoleToggle: (role: RolesDTO) => void;
}

const RolesList = ({
 searchValue,
 setSearchValue,
 selectedGroupId,
 localRoles,
 localRoleIdSet,
 filteredGroupedRoles,
 isLoadingRoles,
 handleGroupToggle,
 handleRoleToggle,
}: RolesListProps) => {
 const { t } = useTranslation();

 const highlightedText = useMemo(() => {
  const value = searchValue.trim();
  if (!value) {
   return null;
  }
  return new RegExp(`(${value})`, 'gi');
 }, [searchValue]);

 const renderHighlightedText = (value: string) => {
  if (!highlightedText) {
   return value || '';
  }
  const parts = (value || '').split(highlightedText);
  const normalizedSearch = searchValue.toLowerCase();
  return parts.map((part, index) =>
   part.toLowerCase() === normalizedSearch ? (
    <Box
     key={`highlight_${part}_${index}`}
     component="span"
     sx={(theme: Theme) => ({
      backgroundColor: theme.palette.warning.light,
      color:
       theme.palette.mode === 'dark'
        ? theme.palette.kemek.white
        : theme.palette.kemek.darkGray,
     })}
    >
     {part}
    </Box>
   ) : (
    part
   ),
  );
 };

 return (
  <Box
   sx={{
    width: '100%',
    flex: 1,
    order: { xs: 2, md: 1 },
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    overflow: 'auto',
   }}
  >
   <Card
    sx={{
     overflow: 'visible',
     position: 'sticky',
     top: 0,
     zIndex: 10,
     borderBottom: (theme: Theme) => `5px solid ${theme.palette.kemek.bg2}`,
    }}
   >
    <CardContent>
     <FieldSearch
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      label={t('global.search')}
     />
    </CardContent>
   </Card>

   {!selectedGroupId && (
    <Card>
     <CardContent>
      <Typography
       sx={(theme: Theme) => ({
        fontSize: 13,
        color: theme.palette.kemek.darkGray,
       })}
      >
       {t('accessGroups.noGroupSelected')}
      </Typography>
     </CardContent>
    </Card>
   )}

   {selectedGroupId && isLoadingRoles && (
    <Card sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
     <CardContent>
      <CircularProgress size={28} />
     </CardContent>
    </Card>
   )}

   {selectedGroupId &&
    !isLoadingRoles &&
    filteredGroupedRoles.length === 0 &&
    !searchValue.trim() && (
     <Card>
      <CardContent>
       <Typography
        sx={(theme: Theme) => ({
         fontSize: 13,
         color: theme.palette.kemek.darkGray,
        })}
       >
        {t('accessGroups.noRoles')}
       </Typography>
      </CardContent>
     </Card>
    )}

   {selectedGroupId &&
    !isLoadingRoles &&
    filteredGroupedRoles.length === 0 &&
    !!searchValue.trim() && (
     <Card>
      <CardContent>
       <Typography
        sx={(theme: Theme) => ({
         fontSize: 13,
         color: theme.palette.kemek.darkGray,
        })}
       >
        {t('global.noResults')}
       </Typography>
      </CardContent>
     </Card>
    )}

   {selectedGroupId &&
    !isLoadingRoles &&
    filteredGroupedRoles.map(([groupName, groupRolesList]) => {
     const allChecked = groupRolesList.every((role) =>
      localRoleIdSet.has(role.Id || ''),
     );
     const someChecked =
      groupRolesList.some((role) => localRoleIdSet.has(role.Id || '')) &&
      !allChecked;

     return (
      <Card
       key={groupName}
       sx={{ overflow: 'visible', position: 'relative', zIndex: 5 }}
      >
       <CardContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}
       >
        <FormControlLabel
         control={
          <Checkbox
           checked={allChecked}
           indeterminate={someChecked}
           onChange={() => handleGroupToggle(groupRolesList)}
           aria-label={`Toggle all ${groupName} roles`}
          />
         }
         label={
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
           {renderHighlightedText(groupName)}
          </Typography>
         }
         sx={(theme: Theme) => ({
          borderBottom: `1px solid ${theme.palette.kemek.lightestGray}`,
          pb: 1,
          width: '100%',
          m: 0,
         })}
        />
        <Box
         sx={{
          display: 'grid',
          gridTemplateColumns: {
           xs: 'repeat(1, 1fr)',
           lg: 'repeat(2, 1fr)',
           xl: 'repeat(4, 1fr)',
          },
          gap: '0 4px',
         }}
        >
         {groupRolesList.map((role) => (
          <Tooltip
           key={role.Id}
           title={role.Description || ''}
           placement="top"
           arrow
           disableHoverListener={!role.Description}
          >
           <FormControlLabel
            control={
             <Checkbox
              checked={localRoleIdSet.has(role.Id || '')}
              onChange={() => handleRoleToggle(role)}
              aria-label={`${role.Name} ${role.Action}`}
             />
            }
            label={
             <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              {renderHighlightedText(role.Action || role.Name || '')}
             </Typography>
            }
            sx={{ m: 0 }}
           />
          </Tooltip>
         ))}
        </Box>
       </CardContent>
      </Card>
     );
    })}
  </Box>
 );
};

export default RolesList;
